import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Popover, Space } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import {
  Avatar, AvatarProps, Body, Button, IconType, Theme, Tooltip, notification, NotificationType,
} from '@routific/hive';
import { useStore } from '../../../store/mst';
import usePermissions, { Features } from '../../../permissions/usePermissions';
import ComponentNames from '../../../permissions/constants/componentNames';
import { PlanningActionsStatus, RoleNames, RouteDtoModelType } from '../../../store/mst/models';
import SelectDriver from './SelectDriver';
import DriverLocationContent from './DriverLocationContent';
import InviteDriver from './InviteDriver';
import UnassignDriver from './UnassignDriver';
import hasAssignedDriver from './helpers/hasAssignedDriver';
// TODO: Move AddMemberModal to a shared location - it's currently nested inside CompanySettings but used here too
import AddMemberModal from '../../CompanySettings/TeamSettingsTab/organisms/AddMemberModal/AddMemberModal';

interface Props {
  route: RouteDtoModelType;
  pending: boolean;
  online?: boolean;
  size?: AvatarProps['size'];
  withLabel: boolean;
  withAssignDriver?: boolean;
  routeDisabled?: boolean;
  placement?: TooltipPlacement;
}

const XS_BUTTON_SIZE = '24px';

const ClickableAvatarWrapper = styled.div<{ disabled?: boolean, defaultCursor?: boolean, deletedDriver?: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  ${({ defaultCursor }) => defaultCursor && `
      cursor: default;
  `}

  ${({ disabled, deletedDriver }) => !disabled && !deletedDriver && `
    .ant-avatar {
      transition: background-color 0.25s ease;

      &:hover, &:focus, &:active {
        border: 1px solid ${Theme.Colors.primary7};
      }

    }`}

    & .ant-badge-status {
      vertical-align: middle;
    }

`;

const Container = styled.span`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledButton = styled(Button) <{ $avatarSize?: AvatarProps['size'] }>`
  box-shadow: none;

  ${({ $avatarSize }) => $avatarSize === 'small' && `
    &.ant-btn-icon-only.ant-btn-sm {
      width: ${XS_BUTTON_SIZE};
      height: ${XS_BUTTON_SIZE};
    }

    &.ant-btn-circle.ant-btn-sm {
      min-width: ${XS_BUTTON_SIZE};
    }
 `}

  &:not(.hive-btn-hovered):not([disabled]) {
    background-color: ${Theme.Colors.gray2};
    border-color: transparent;
  }
  transition: border-color .1s ease-in-out;
`;

const DriverList = styled.div`
  width: 420px;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;

  &:has([data-cy='select-driver-empty']) {
    .driver-list {
      display: none;
    }
  }

 .ant-select-dropdown {
    position: relative;
    width: 100%;
    border-radius: 0;
    box-shadow: none;
    padding: 0;
    border: none;
    margin: 0;

    .ant-select-item-option-selected:not(.ant-select-item-option-disabled),
    .ant-select-item-option-selected .ant-select-item-option-content,
    .ant-select-item-option-selected .ant-typography {
      font-weight: 400 !important;
    }

    .ant-select-item {
      padding: 0;
    }

    .ant-select-item-option-content {
      padding: 0;
    }
  }

  .driver-list {
    width: 100%;
    max-height: 256px;
    margin: 0;
    padding: 0;
    overflow: hidden;

    &> div {
      position: relative !important;
      width: 100%;
      overflow: hidden;
      margin: 0;
      padding: 0;
      border: none;
    }
  }
`;

const AssignedDriverName = styled(Body) <{ deleted?: boolean, complete?: boolean }>`
  &.ant-typography-ellipsis {
    max-width: 120px;
  }
  color: ${({ deleted, complete }) => {
    if (deleted) {
      if (complete) return Theme.Colors.textInvalid;
      return Theme.Colors.danger5;
    }
    return 'initial';
  }};
`;

const StyledSelectWrapper = styled.div`
  align-items: baseline;
  width: auto !important;
  padding: ${Theme.Spacing.mdSpacing} ${Theme.Spacing.mdSpacing} ${Theme.Spacing.smSpacing} ${Theme.Spacing.mdSpacing};
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${Theme.Colors.gray4};
`;

const getAssignedDriverNameType = (activeTrigger: boolean | undefined, deletedDriver: boolean, complete: boolean | undefined) => {
  if (deletedDriver) return undefined;
  if (complete) return 'invalid';
  if (activeTrigger) return 'accent';
  return undefined;
};

const AssignedTo: React.FC<Props> = ({
  route, pending, online, size, withLabel, withAssignDriver = true, routeDisabled, placement,
}) => {
  const { t } = useTranslation('components');
  const store = useStore();
  const { model, ui } = store;

  const {
    canAssignDriversToRoutes,
  } = usePermissions(Features.Routes, ComponentNames.AssignedTo);

  const { isStarted, finished } = route;
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const [hover, setHover] = useState<boolean>(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false);
  const [isUnassigning, setIsUnassigning] = useState<boolean>(false);
  const isModalOpenRef = useRef<boolean>(false);
  const activeTrigger = (popupVisible || hover) && canAssignDriversToRoutes;
  const disabled = pending || !canAssignDriversToRoutes;
  const clickableAvatarDefaultCursor = isStarted || finished;
  const driverAssigned = hasAssignedDriver(route);

  let popoverOverlayClassName = 'assign-driver-popover';
  let popoverPlacement: TooltipPlacement = placement || 'rightTop';

  const openAddMemberModal = () => {
    isModalOpenRef.current = true;
    setShowAddMemberModal(true);
  };

  const closeAddMemberModal = () => {
    isModalOpenRef.current = false;
    setShowAddMemberModal(false);
  };

  const handleUnassignClick = async () => {
    if (!route.driver || !route.uuid || !ui.selectedWorkspace?.id || isUnassigning) {
      return;
    }

    try {
      setIsUnassigning(true);
      const response: any = await model.unassignDriverFromRoute(ui.selectedWorkspace.id, route.uuid);

      if (response.unassignDriverFromRoute?.status === PlanningActionsStatus.FAILED) {
        notification({
          id: 'unassign-driver-error',
          type: NotificationType.ERROR,
          message: t('assignDriver.notifications.unassignError'),
        });
        return;
      }

      model.refreshCalendarRoutes();
      setPopupVisible(false);
    } catch (error) {
      notification({
        id: 'unassign-driver-error',
        type: NotificationType.ERROR,
        message: t('assignDriver.notifications.unassignError'),
      });
    } finally {
      setIsUnassigning(false);
    }
  };

  let popoverContent = (
    <DriverList>
      <StyledSelectWrapper>
        <SelectDriver route={route} setPopupVisible={setPopupVisible} />
      </StyledSelectWrapper>
      <div className="driver-list" />
      <FooterActions data-cy="assign-driver-footer-actions">
        {driverAssigned && <UnassignDriver onUnassignClick={handleUnassignClick} disabled={isUnassigning || pending} driverName={route.driver?.name ?? ''} />}
        <InviteDriver onInviteClick={openAddMemberModal} />
      </FooterActions>
      <AddMemberModal
        visible={showAddMemberModal}
        onClose={closeAddMemberModal}
        defaultRole={RoleNames.DRIVER}
      />
    </DriverList>
  );
  let popoverTrigger = ((isStarted && driverAssigned) || disabled) ? 'none' : 'click';

  if (isStarted && route.driver?.name && !finished) {
    popoverOverlayClassName = 'driver-location-popover';
    popoverPlacement = 'top';
    popoverContent = (
      <DriverLocationContent driverName={route.driver.name} lastLocationUpdateMinutes={route.lastLocationUpdateMinutes} />
    );
    popoverTrigger = 'hover';
  }

  const getTooltipText = () => {
    let key = '';
    if (pending) {
      key = 'locked';
    } else if (isStarted && route.driver === undefined) {
      key = 'routeStarted';
    } else if (finished) {
      key = 'routeFinished';
    } else {
      return '';
    }
    return t(`assignDriver.tooltips.${key}`);
  };

  const assignedDriverAvatar = () => (withLabel ? (
    <ClickableAvatarWrapper disabled={disabled} defaultCursor={clickableAvatarDefaultCursor} deletedDriver={!!route.driver?.deleted}>
      <Space align="center">
        <Avatar
          data-cy="assign-driver-avatar"
          online={online}
          size={size}
          bordered={activeTrigger}
          disabled={routeDisabled}
          deleted={!!route.driver?.deleted}
          complete={finished}>
          {route.driver?.initials}
        </Avatar>
        <AssignedDriverName
          type={getAssignedDriverNameType(activeTrigger, !!route.driver?.deleted, finished)}
          ellipsis data-cy="assign-driver-body"
          disabled={routeDisabled}
          deleted={!!route.driver?.deleted}
          complete={finished}>
          {route.driver?.name}
        </AssignedDriverName>
      </Space>
    </ClickableAvatarWrapper>
  ) : (
    <ClickableAvatarWrapper disabled={disabled} defaultCursor={clickableAvatarDefaultCursor} deletedDriver={!!route.driver?.deleted}>
      <Avatar
        online={online}
        size={size}
        bordered={activeTrigger}
        disabled={routeDisabled}
        deleted={!!route.driver?.deleted}
        complete={finished}>
        {route.driver?.initials}
      </Avatar>
    </ClickableAvatarWrapper>
  ));

  const assignDriverButton = () => {
    if (!withAssignDriver) {
      return null;
    }
    return (withLabel ? (
      <Space align="center">
        <StyledButton
          icon={IconType.Add}
          shape="circle"
          type="default"
          size="small"
          $avatarSize={size}
          disabled={disabled}
          data-cy="assign-driver-btn"
          data-testid="assign-driver-btn"
          isHovered={!!activeTrigger}
        />
        <Body data-cy="assign-driver-label" type={activeTrigger ? 'accent' : undefined}>
          {t('assignDriver.title')}
        </Body>
      </Space>
    ) : (
      <StyledButton
        icon={IconType.Add}
        shape="circle"
        type="default"
        size="small"
        $avatarSize={size}
        disabled={disabled}
        data-cy="assign-driver-btn"
        data-testid="assign-driver-btn"
        isHovered={!!activeTrigger}
      />
    ));
  };

  const handlePopoverVisibleChange = (visible: boolean) => {
    // Don't close the popover when the modal is open (using ref for synchronous check)
    if (!visible && isModalOpenRef.current) {
      return;
    }
    setPopupVisible(visible);
  };

  return (
    <Container data-cy="assign-driver" onClick={e => { e.stopPropagation(); }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <Tooltip
        overlayClassName="cy-assign-driver-tooltip"
        placement="left"
        title={getTooltipText()}
        disabled={!!isStarted && !!route.driver}
      >
        <Popover
          overlayClassName={popoverOverlayClassName}
          placement={popoverPlacement}
          content={popoverContent}
          trigger={popoverTrigger as any} // TODO: fix type
          visible={popupVisible}
          onVisibleChange={handlePopoverVisibleChange}
          destroyTooltipOnHide
        >
          {route.driver ? assignedDriverAvatar() : assignDriverButton()}
        </Popover>
      </Tooltip>
    </Container>
  );
};

export default observer(AssignedTo);
